import React from "react";
import Field from "./Field";
import { INDIAN_STATE_OPTIONS } from "../../constants/enums";

/**
 * Reusable address fieldset. Renders the eight standard address fields the BE
 * expects (buildingNo, streetName1, streetName2, city, district, state,
 * pinCode, country) under a configurable name prefix so the parent's RHF
 * state stays the same shape as the BE schema.
 *
 *   <AddressFields prefix="address" />
 *     → captures values at address.buildingNo, address.streetName1, ...
 *
 * The State field uses INDIAN_STATE_OPTIONS as a searchable dropdown — same
 * source of truth as every other "select Indian state" surface.
 *
 * Pass `required` to mark specific fields as required visually (yup schema
 * still owns actual validation). `required={true}` marks the common subset
 * (city, district, state, pinCode); pass an array for finer control.
 */
const REQUIRED_DEFAULT = ["city", "district", "state", "pinCode"];

const AddressFields = ({ prefix = "address", required = false }) => {
  const requiredSet = new Set(
    required === true ? REQUIRED_DEFAULT : Array.isArray(required) ? required : [],
  );
  const isReq = (name) => requiredSet.has(name);
  const n = (name) => (prefix ? `${prefix}.${name}` : name);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field name={n("buildingNo")} label="Building No" placeholder="Building number" required={isReq("buildingNo")} />
        <Field name={n("streetName1")} label="Street Name 1" placeholder="Street name" required={isReq("streetName1")} />
      </div>
      <Field name={n("streetName2")} label="Street Name 2" placeholder="Street name 2 (optional)" required={isReq("streetName2")} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field name={n("city")} label="City" placeholder="City" required={isReq("city")} />
        <Field name={n("district")} label="District" placeholder="District" required={isReq("district")} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field
          name={n("state")}
          label="State"
          type="select"
          options={INDIAN_STATE_OPTIONS}
          searchable
          required={isReq("state")}
          placeholder="Select state"
        />
        <Field name={n("pinCode")} label="Pin Code" placeholder="Pin code" required={isReq("pinCode")} />
        <Field name={n("country")} label="Country" placeholder="Country" required={isReq("country")} />
      </div>
    </div>
  );
};

export default AddressFields;
